import useSWRV from 'swrv';
import { Ref } from '@vue/composition-api';
import http, { HTTPResultAsync } from '@/utils/http';
import { AnalyticsAppType, analyticsManager } from '@/utils/mixins/analytics';
import useSwrvState from '@/utils/useSwrvState';
import { SendResetPasswordEmailRequest } from './dto/SendResetPasswordEmail';
import { ResetPasswordRequest } from './dto/ResetPassword';
import { VerifyRequest } from './dto/Verify';
import { SignInRequest } from './dto/SignIn';
import { SetPasswordRequest } from './dto/SetPassword';
import { VerifyPwdResetTokenRequest } from './dto/VerifyToken';
import { Set2FARequest } from './dto/Set2FA';
import { SendUnlockAccountEmailRequest } from './dto/sendUnlockAccountEmail';
import { GoogleSignInRequest } from './dto/GoogleSignIn';
import { MagicLinkRequest } from './dto/MagicLink';

export async function me(): HTTPResultAsync {
  return http.sendSafe('GET', '/auth/me');
}

export async function generateIntercomIdentityHash(): HTTPResultAsync {
  return http.sendSafe('GET', '/auth/intercom-hash');
}

export async function _me() {
  const resp = await http.send('GET', '/auth/me');
  return resp.payload;
}

function getMeSwr(isLoggedIn: Ref<boolean>) {
  const {
    data,
    error,
    mutate,
    isValidating,
  } = useSWRV(
    // fire this request only when user is logged in
    // @ts-ignore
    () => isLoggedIn.value && '/auth/me',
    () => _me(),
    {
      revalidateOnFocus: true,
    },
  );

  const swrState = useSwrvState(data, error, isValidating);
  return {
    data,
    error,
    mutate,
    isValidating,
    swrState,
  };
}

export const swr = {
  meSwr: getMeSwr,
};

export async function signIn(payload: SignInRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/signin', payload);
}

export async function signInWithMagicLink(payload: MagicLinkRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/signin/magic-link', payload);
}

export async function signInGoogle(payload: GoogleSignInRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/google-signin', payload);
}

export async function signUpGoogle(payload: GoogleSignInRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/google-signup', payload);
}

export async function signOut(): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/signout');
}

export async function verify(payload: VerifyRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/verify', payload);
}

export async function verifyToken(payload: VerifyPwdResetTokenRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/verify/reset-token', payload);
}

export async function sendResetPasswordEmail(payload: SendResetPasswordEmailRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/forgot-password', payload);
}

export async function sendUnlockAccountEmail(payload: SendUnlockAccountEmailRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/request-unlock', payload);
}

export async function resetPassword(payload: ResetPasswordRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/reset-password', payload);
}

export async function setPassword(payload: SetPasswordRequest): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/set-password', payload);
}

export async function issueVerificationCode(): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/verify/issue');
}

export async function initiate2FA(): HTTPResultAsync {
  return http.sendSafe('POST', '/auth/2fa/initiate');
}

export async function enable2FA(payload: Set2FARequest): HTTPResultAsync {
  const result = await http.sendSafe('POST', '/auth/2fa/enable', payload);
  if (result.isOk()) {
    analyticsManager.setUserProfile(AnalyticsAppType.USER, {
      custom: {
        MFA: true,
      },
    });
  }
  return result;
}

export async function disable2FA(payload: Set2FARequest): HTTPResultAsync {
  const result = await http.sendSafe('POST', '/auth/2fa/disable', payload);
  if (result.isOk()) {
    analyticsManager.setUserProfile(AnalyticsAppType.USER, {
      custom: {
        MFA: false,
      },
    });
  }
  return result;
}
